import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { easeSoft } from "../lib/motion";

export default function PhotoGallery({
  images,
  crecheNom,
}: {
  images: string[];
  crecheNom: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const slideWidth = () => {
    const first = trackRef.current?.firstElementChild as HTMLElement | null;
    return first ? first.offsetWidth + 16 : 0;
  };

  const scrollTo = (i: number) => {
    const track = trackRef.current;
    const w = slideWidth();
    if (!track || !w) return;
    const target = Math.max(0, Math.min(images.length - 1, i));
    track.scrollTo({ left: target * w, behavior: "smooth" });
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const w = slideWidth();
      if (!w) return;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      setActive(atEnd ? images.length - 1 : Math.round(track.scrollLeft / w));
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, [images.length]);

  const step = useCallback(
    (d: number) =>
      setLightbox((i) => (i === null ? i : (i + d + images.length) % images.length)),
    [images.length],
  );

  useEffect(() => {
    if (lightbox === null) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, step]);

  const arrow =
    "hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white/90 backdrop-blur text-ink shadow-soft-md hover:bg-white transition disabled:opacity-0 disabled:pointer-events-none";

  return (
    <div>
      <div className="relative">
        <div
          ref={trackRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {images.map((src, i) => (
            <button
              type="button"
              key={`${i}-${src}`}
              onClick={() => setLightbox(i)}
              className="group snap-start shrink-0 w-[86%] sm:w-[28rem] md:w-[40rem] lg:w-[46rem] aspect-[3/2] rounded-4xl overflow-hidden shadow-soft-md bg-muted cursor-zoom-in focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sage/50"
              aria-label={`Agrandir la photo ${i + 1} sur ${images.length}`}
            >
              <img
                src={src}
                alt={`${crecheNom} — photo ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                loading={i < 2 ? "eager" : "lazy"}
                draggable={false}
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`${arrow} left-4`}
          onClick={() => scrollTo(active - 1)}
          disabled={active === 0}
          aria-label="Photo précédente"
        >
          <ChevronLeft strokeWidth={2} />
        </button>
        <button
          type="button"
          className={`${arrow} right-4`}
          onClick={() => scrollTo(active + 1)}
          disabled={active >= images.length - 1}
          aria-label="Photo suivante"
        >
          <ChevronRight strokeWidth={2} />
        </button>
      </div>

      {images.length > 1 && (
        <div className="mt-5 flex justify-center gap-2" role="tablist" aria-label="Photos">
          {images.map((src, i) => (
            <button
              type="button"
              key={`${i}-${src}`}
              role="tab"
              aria-selected={i === active}
              aria-label={`Photo ${i + 1}`}
              onClick={() => scrollTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === active ? "w-7 bg-sage" : "w-2 bg-edge-strong/70 hover:bg-edge-strong"
              }`}
            />
          ))}
        </div>
      )}

      {createPortal(
        <AnimatePresence>
          {lightbox !== null && (
            <motion.div
              className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: easeSoft }}
              onClick={() => setLightbox(null)}
              role="dialog"
              aria-modal="true"
              aria-label={`${crecheNom} — photo ${lightbox + 1} sur ${images.length}`}
            >
              <motion.img
                key={lightbox}
                src={images[lightbox]}
                alt={`${crecheNom} — photo ${lightbox + 1}`}
                className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25, ease: easeSoft }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                aria-label="Fermer"
              >
                <X strokeWidth={2} />
              </button>
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      step(-1);
                    }}
                    className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                    aria-label="Photo précédente"
                  >
                    <ChevronLeft strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      step(1);
                    }}
                    className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                    aria-label="Photo suivante"
                  >
                    <ChevronRight strokeWidth={2} />
                  </button>
                  <p className="absolute bottom-5 inset-x-0 text-center text-sm text-white/80">
                    {lightbox + 1} / {images.length}
                  </p>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}
